/**
 * Grab-and-pan for a wide table — drag anywhere in the body to scroll it
 * in BOTH axes, like the PHP portal's `table.js`.
 *
 * Extracted from `useLogsTableInteractions` (2026-08-11) so the Issues log
 * gets the identical gesture instead of a second implementation. That hook
 * also owns column resize and freeze, which Issues does not use — panning
 * was the only part worth sharing, and it carries two fixes that are easy
 * to lose in a rewrite:
 *
 *   · `clientX/clientY`, never `pageX/pageY`. Page coords are
 *     document-relative, so the moment this handler scrolls the window the
 *     coordinate shifts under a stationary mouse: the next mousemove reads
 *     a delta the user never made, scrolls back, and the row jitters. That
 *     was the "vibrating" vertical drag reported on 2026-08-10.
 *
 *   · The vertical scroll target is re-resolved on every mousedown, not
 *     once at mount. `Shell.tsx`'s `<main>` reports `overflowY: auto`
 *     (coerced from `visible` because it sets `overflow-x`, per spec) while
 *     never actually scrolling, so a mount-time walk latched onto it and
 *     silently no-op'd every vertical pan for the whole session. Geometry
 *     is trusted over computed style, and rows streaming in can flip the
 *     answer between gestures.
 */
import { useEffect } from 'react';

/** Movement before a press counts as a drag rather than a click. */
const DRAG_THRESHOLD_PX = 4;
/** How long a post-drag click stays swallowed (PHP uses 80ms). */
const DRAG_SUPPRESS_MS = 80;
/** Elements whose own press must not start a pan. */
const DRAG_SKIP_SELECTOR =
  'button, a, input, select, textarea, th, .row-check, [role="button"]';

/**
 * Marks a scroller that installs its OWN pan and must not be double-bound
 * by the global delegate. The logs table qualifies: `useLogsTableInteractions`
 * pans it as part of the same gesture set that owns column resize + freeze.
 */
export const PAN_OPT_OUT_ATTR = 'data-logs-scroller';

/**
 * Marks an element that matches DRAG_SKIP_SELECTOR but should still start a
 * pan — a whole ROW rendered as a `<button>`, for instance.
 *
 * The skip list protects real controls: a checkbox or a link must be
 * clickable without the table sliding away. A row-sized button is the
 * opposite case — it IS the surface you want to grab, and because every
 * mousedown lands on one, the issues table could not be panned at all while
 * the logs table (whose rows are `<tr>`) could. The drag/click split is
 * already handled: a real drag swallows the click that follows, so opting a
 * row in costs nothing.
 */
export const PAN_THROUGH_ATTR = 'data-pan-through';

/** Every horizontally-scrolling table shell in the app carries this. */
export const TABLE_SCROLLER_SELECTOR = '.hub-table-wrap';

export interface UseTableDragPanOptions {
  /** Extra selectors that should not initiate a pan (resize handles etc.). */
  skipSelector?: string;
  /** Set false to disable the gesture without unmounting the host. */
  enabled?: boolean;
}

export function useTableDragPan(
  scroller: HTMLElement | null,
  { skipSelector, enabled = true }: UseTableDragPanOptions = {},
): void {
  useEffect(() => {
    const cnt = scroller;
    if (!cnt || !enabled) return;

    function resolveVerticalScroller(): { useWindow: boolean; vEl: HTMLElement } {
      let walker: HTMLElement | null = cnt!.parentElement;
      while (walker && walker !== document.body) {
        const ov = getComputedStyle(walker).overflowY;
        if (ov === 'auto' || ov === 'scroll') {
          // An unbounded <main> can claim overflowY:auto while its content
          // exactly fits. Trust the geometry, not the style. The +1
          // swallows sub-pixel rounding in Safari/Chrome.
          if (walker.scrollHeight > walker.clientHeight + 1) {
            return { useWindow: false, vEl: walker };
          }
        }
        walker = walker.parentElement;
      }
      return {
        useWindow: true,
        vEl:
          (document.scrollingElement as HTMLElement | null) ??
          document.documentElement,
      };
    }

    let isDown = false;
    let startX = 0;
    let startY = 0;
    let scrollLeft = 0;
    let scrollTop = 0;
    let dragged = false;
    let useWindow = true;
    let vEl: HTMLElement =
      (document.scrollingElement as HTMLElement | null) ??
      document.documentElement;

    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const skip = target.closest<HTMLElement>(DRAG_SKIP_SELECTOR);
      if (skip && !skip.hasAttribute(PAN_THROUGH_ATTR)) return;
      if (skipSelector && target.closest(skipSelector)) return;
      const resolved = resolveVerticalScroller();
      useWindow = resolved.useWindow;
      vEl = resolved.vEl;
      isDown = true;
      dragged = false;
      startX = e.clientX;
      startY = e.clientY;
      scrollLeft = cnt.scrollLeft;
      scrollTop = useWindow ? window.scrollY : vEl.scrollTop;
      cnt.style.cursor = 'grabbing';
      cnt.style.userSelect = 'none';
    };

    const endDrag = () => {
      if (!isDown) return;
      isDown = false;
      cnt.style.cursor = '';
      cnt.style.userSelect = '';
      if (dragged) {
        // Swallow the next click so a row's own onClick (open the drawer)
        // doesn't fire at the end of a pan. Capture-phase, so it beats the
        // bubble handler on the <tr>.
        const swallow = (ev: MouseEvent) => {
          ev.stopPropagation();
          ev.preventDefault();
        };
        cnt.addEventListener('click', swallow, { capture: true, once: true });
        window.setTimeout(() => {
          cnt.removeEventListener('click', swallow, { capture: true });
        }, DRAG_SUPPRESS_MS);
      }
      dragged = false;
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      const walkX = e.clientX - startX;
      const walkY = e.clientY - startY;
      if (
        Math.abs(walkX) > DRAG_THRESHOLD_PX ||
        Math.abs(walkY) > DRAG_THRESHOLD_PX
      ) {
        dragged = true;
        e.preventDefault();
        cnt.scrollLeft = scrollLeft - walkX;
        if (useWindow) {
          window.scrollTo({
            top: scrollTop - walkY,
            behavior: 'instant' as ScrollBehavior,
          });
        } else {
          vEl.scrollTop = scrollTop - walkY;
        }
      }
    };

    cnt.addEventListener('mousedown', onMouseDown);
    cnt.addEventListener('mouseleave', endDrag);
    cnt.addEventListener('mouseup', endDrag);
    cnt.addEventListener('mousemove', onMouseMove);
    return () => {
      cnt.removeEventListener('mousedown', onMouseDown);
      cnt.removeEventListener('mouseleave', endDrag);
      cnt.removeEventListener('mouseup', endDrag);
      cnt.removeEventListener('mousemove', onMouseMove);
    };
  }, [scroller, skipSelector, enabled]);
}


/**
 * One delegated pan for EVERY table in the app.
 *
 * Owner directive (2026-08-11): "all tables should allow these movements
 * like grabbing to move — cancelled, no-shows, issues log etc". 33 files
 * render a `.hub-table-wrap`, and wiring a ref + hook into each would be 33
 * chances to miss one and 33 more for the next table anybody adds.
 *
 * Delegation instead: a single mousedown listener on `document` resolves
 * the scroller with `closest()`, so a table mounted by a route that did not
 * exist when this ran is covered for free. Mounted once, in `Shell`.
 *
 * Tables that pan themselves opt out via `PAN_OPT_OUT_ATTR` — binding twice
 * would apply every delta twice and scroll at double speed.
 */
export function useGlobalTableDragPan(): void {
  useEffect(() => {
    let cnt: HTMLElement | null = null;
    let isDown = false;
    let startX = 0;
    let startY = 0;
    let scrollLeft = 0;
    let scrollTop = 0;
    let dragged = false;
    let useWindow = true;
    let vEl: HTMLElement =
      (document.scrollingElement as HTMLElement | null) ??
      document.documentElement;

    function resolveVertical(from: HTMLElement): { useWindow: boolean; vEl: HTMLElement } {
      let walker: HTMLElement | null = from.parentElement;
      while (walker && walker !== document.body) {
        const ov = getComputedStyle(walker).overflowY;
        if ((ov === 'auto' || ov === 'scroll') &&
            walker.scrollHeight > walker.clientHeight + 1) {
          return { useWindow: false, vEl: walker };
        }
        walker = walker.parentElement;
      }
      return {
        useWindow: true,
        vEl:
          (document.scrollingElement as HTMLElement | null) ??
          document.documentElement,
      };
    }

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;                  // left button only
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const skip = target.closest<HTMLElement>(DRAG_SKIP_SELECTOR);
      if (skip && !skip.hasAttribute(PAN_THROUGH_ATTR)) return;
      const scroller = target.closest<HTMLElement>(TABLE_SCROLLER_SELECTOR);
      if (!scroller || scroller.hasAttribute(PAN_OPT_OUT_ATTR)) return;
      cnt = scroller;
      const resolved = resolveVertical(scroller);
      useWindow = resolved.useWindow;
      vEl = resolved.vEl;
      isDown = true;
      dragged = false;
      startX = e.clientX;
      startY = e.clientY;
      scrollLeft = scroller.scrollLeft;
      scrollTop = useWindow ? window.scrollY : vEl.scrollTop;
      scroller.style.cursor = 'grabbing';
      scroller.style.userSelect = 'none';
    };

    const endDrag = () => {
      if (!isDown || !cnt) return;
      const scroller = cnt;
      isDown = false;
      scroller.style.cursor = '';
      scroller.style.userSelect = '';
      if (dragged) {
        const swallow = (ev: MouseEvent) => {
          ev.stopPropagation();
          ev.preventDefault();
        };
        scroller.addEventListener('click', swallow, { capture: true, once: true });
        window.setTimeout(() => {
          scroller.removeEventListener('click', swallow, { capture: true });
        }, DRAG_SUPPRESS_MS);
      }
      dragged = false;
      cnt = null;
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDown || !cnt) return;
      const walkX = e.clientX - startX;
      const walkY = e.clientY - startY;
      if (Math.abs(walkX) > DRAG_THRESHOLD_PX || Math.abs(walkY) > DRAG_THRESHOLD_PX) {
        dragged = true;
        e.preventDefault();
        cnt.scrollLeft = scrollLeft - walkX;
        if (useWindow) {
          window.scrollTo({
            top: scrollTop - walkY,
            behavior: 'instant' as ScrollBehavior,
          });
        } else {
          vEl.scrollTop = scrollTop - walkY;
        }
      }
    };

    // Move/up on `document`, not on the scroller: a fast drag outruns the
    // pointer and leaves the element, and a scroller-bound mouseup would
    // never fire — the table would stay latched in grabbing state.
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', endDrag);
    window.addEventListener('blur', endDrag);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', endDrag);
      window.removeEventListener('blur', endDrag);
    };
  }, []);
}
